import { useMemo, useState } from 'react';
import {
  ERROR_LANES,
  LANE_REGISTRY,
  customerCodes,
  lanesWithCodes,
  type ErrorLane,
  type ErrorReasonEntry,
  isCustomerFacing,
  GENERIC_CUSTOMER_MESSAGE,
} from '@infras/shared-types';
import { useErrorCodeDocs } from '../components/ErrorCodeArticle';
import { SortHeader } from '../components/SortHeader';
import { Link } from 'react-router';
import { compareBy, useUrlClear, useUrlSort, useUrlState } from '../lib/useUrlState';

/**
 * Error dictionary — the codes we show customers, and the copy that goes with each.
 *
 * This page has been through three shapes, and the two that failed both failed the
 * SAME WAY, so the reasoning is worth keeping.
 *
 *  1. CARDS. Every entry a card with three paragraphs of rationale in the body. You
 *     cannot scan for a code in a wall of prose.
 *  2. TABLES. One collapsible table per lane, each with its own header strip, badges
 *     and pager, plus a second registry table underneath — 26 rows arriving wrapped
 *     in four table headers and two pagers.
 *  3. ONE table, /clusters-shaped: stat row, one toolbar, one sortable table, one
 *     pager. Better, and still wrong.
 *
 * What killed (3) is what this layout exists to fix. A `<table>` gives every column
 * the same height and forces the widest cell to set the row, so a column of 2-3
 * SENTENCE messages had to be truncated to one line to keep the rows scannable —
 * and a dictionary that truncates the copy is a dictionary that never answers the
 * question you opened it with. It also could not be contained: because a table's
 * intrinsic width is the sum of its columns, the message column's measure pushed
 * the table clean through `--page-max` (791px of table in a 398px page), which is
 * the "mid-row canyon" the layout rules warn about, at page scale. Row heights
 * still ranged 76-143px because wrapping was left to chance.
 *
 * So: a DEFINITION LIST, which is what Stripe's error reference — the model this
 * file's own contract cites — actually is. A narrow fixed identity column on the
 * left keeps the codes aligned and scannable down one edge; the copy sits on the
 * right at a reading measure and is shown IN FULL. Nothing is truncated, so nothing
 * has to be un-truncated by an interaction. It is a CSS grid, not a table, so it
 * cannot overflow its container and each entry is free to be exactly as tall as its
 * own copy.
 *
 * The pager and the column picker went with the table. Twenty-six entries do not
 * need paginating — the filters and the browser's own find are the right tools at
 * this size — and a column picker over two columns is a control with nothing to do.
 *
 * Entries stay in DECLARATION order by default. Each lane declares them grouped by
 * severity and taxonomy class (ours first, then caller-actionable, then side
 * effects) and that grouping is information; alphabetical is one select away.
 *
 * There is NO second surface. A drawer sat here briefly and could not justify
 * itself: once the list showed the copy in full, the only fields left that were not
 * already on the row were `note`, `taxonomy`, `evt` and `repo` — four values, two of
 * them a single word — and it opened by re-printing the very sentence you had just
 * clicked. A panel whose lead section duplicates the row that summoned it is
 * ceremony, not information.
 *
 * So the internals fold into the row, behind ONE toolbar toggle rather than a
 * per-row interaction. The page serves two readers — support wants code → copy →
 * article, engineering wants reason → class → note → evt for alert rules — and a
 * toggle serves both at their own density without hiding anything from either. Off,
 * it is a clean two-column dictionary; on, every entry carries its own provenance.
 * Nothing is behind a click, so nothing can be missed by not clicking.
 *
 * The article URL is edited exactly where its absence is visible, on the row that
 * says it is missing. Burying the only editable field in a panel meant the gap was
 * on one surface and its fix on another.
 */

/** One entry: the reason row, plus the lane that declares it. */
interface Row {
  lane: ErrorLane;
  entry: ErrorReasonEntry;
}

/**
 * An entry's identity is (lane, reason) — NOT its code. 26 entries carry only 16
 * distinct codes, because the dictionary reuses a code across lanes wherever the
 * customer experience is identical (`unexpected_error` is issued by four). reason
 * is unique within a lane, so this pair is the real key, and it is what the URL
 * carries.
 */
const rowId = (r: Row) => `${r.lane.key}:${r.entry.reason}`;

/** Declaration order, flattened. This is the default order. */
const ALL_ROWS: Row[] = customerCodes();

const SORTS: Record<string, { label: string; pick?: (r: Row) => unknown }> = {
  '': { label: 'Declaration order' },
  code: { label: 'Code A–Z', pick: (r) => r.entry.code },
  level: { label: 'Level', pick: (r) => ['error', 'warn', 'info'].indexOf(r.entry.level) },
  lane: { label: 'Lane', pick: (r) => r.lane.title },
};

export function ErrorCodesPage() {
  // view / filters / sort / selection in the URL, so a narrowed dictionary — or one
  // specific entry — is a link you can paste.
  const [view, setView] = useUrlState('view', 'codes');
  const [q, setQ] = useUrlState('q');
  const [laneFilter, setLaneFilter] = useUrlState('lane');
  const [levelFilter, setLevelFilter] = useUrlState('level');
  const [order, setOrder] = useUrlState('order');

  // Read-only here: the stat needs to know which codes lack an article; editing
  // one is the detail page's job.
  const { docs, docFor } = useErrorCodeDocs();

  const withCodes = lanesWithCodes();
  const distinctCodes = useMemo(() => new Set(ALL_ROWS.map(({ entry }) => entry.code)), []);
  // Distinct CODES, not entries — an article is per code, so counting one
  // undocumented code once per lane that issues it inflates the gap.
  const missing = useMemo(() => {
    const undocumented = new Set<string>();
    for (const { entry } of ALL_ROWS) if (!docFor(entry)) undocumented.add(entry.code);
    return undocumented.size;
  }, [docs]);

  const needle = q.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      ALL_ROWS.filter(
        ({ lane, entry }) =>
          (!needle ||
            entry.code.toLowerCase().includes(needle) ||
            entry.reason.toLowerCase().includes(needle) ||
            entry.customerMessage.toLowerCase().includes(needle) ||
            entry.taxonomy.includes(needle) ||
            lane.evt.includes(needle)) &&
          (!laneFilter || lane.key === laneFilter) &&
          (!levelFilter || entry.level === levelFilter),
      ),
    [needle, laneFilter, levelFilter],
  );
  const pick = SORTS[order]?.pick;
  const sorted = pick ? compareBy(filtered, pick, 'asc') : filtered;
  // Sections only make sense while the list is in declaration order.
  const grouped = !pick;

  const anyFilter = Boolean(needle || laneFilter || levelFilter);
  const clearFilters = useUrlClear(['q', 'lane', 'level']);

  return (
    <div className="page err-dict">
      <div className="stat-row">
        <Stat label="Reasons" value={ALL_ROWS.length} />
        <Stat label="Distinct codes" value={distinctCodes.size} />
        <Stat label="Codes with no article" value={missing} />
        <Stat label="Lanes with codes" value={`${withCodes.size} / ${LANE_REGISTRY.length}`} />
      </div>

      <p className="detail-desc ec-intro">
        <code>reason</code> is internal and renameable — alert rules filter on it;{' '}
        <code>code</code> is what a customer sees and quotes, and never changes. One entry
        per reason, so a code reused across lanes appears more than once.
      </p>

      <div className="toolbar">
        <div className="view-toggle">
          <button className={view === 'codes' ? 'active' : ''} onClick={() => setView('codes')}>
            Codes
          </button>
          <button className={view === 'lanes' ? 'active' : ''} onClick={() => setView('lanes')}>
            Lanes
          </button>
        </div>
        {view === 'codes' && (
          <div className="filter-row" style={{ flex: 1 }}>
            <input
              type="search"
              placeholder="Search code, reason, or wording…"
              aria-label="Search error codes"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select
              className="class-select"
              value={laneFilter}
              onChange={(e) => setLaneFilter(e.target.value)}
              title="filter by lane"
            >
              <option value="">All lanes</option>
              {ERROR_LANES.map((l) => (
                <option key={l.key} value={l.key}>
                  {l.title}
                </option>
              ))}
            </select>
            <select
              className="class-select"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              title="filter by level"
            >
              <option value="">All levels</option>
              <option value="error">error</option>
              <option value="warn">warn</option>
              <option value="info">info</option>
            </select>
            <select
              className="class-select"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              title="sort order"
            >
              {Object.entries(SORTS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
            <span className="filter-count">
              {filtered.length} of {ALL_ROWS.length}
            </span>
          </div>
        )}
      </div>

      {view === 'lanes' ? (
        <LaneRegistry withCodes={withCodes} />
      ) : sorted.length === 0 ? (
        <p className="muted">
          Nothing matches these filters. A code in a log line but not here means the
          dictionary is out of date, not the line.{' '}
          {anyFilter && (
            <button className="btn btn-sm" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </p>
      ) : (
        <div className="ec-list">
          <div className="ec-list__head" aria-hidden="true">
            <span>Code</span>
            <span>What the customer is told</span>
          </div>
          {/* Declaration order is ALREADY grouped by lane — customerCodes() flattens
              lane by lane, so the 13 mdm entries precede the 13 rest ones. The
              headings label a grouping that was already there and invisible, and
              they carry the provenance (which repo's module these were transcribed
              from) that the audit workflow needs.

              A sort deliberately crosses lanes, so it flattens and the per-row lane
              badge comes back. Sections that survived sorting would be lying about
              the order. */}
          {grouped
            ? ERROR_LANES.map((lane) => {
                const rows = sorted.filter((r) => r.lane.key === lane.key);
                if (rows.length === 0) return null;
                return (
                  <section key={lane.key} className="ec-group">
                    <header className="ec-group__head">
                      <span className="ec-group__title">{lane.title}</span>
                      <span className="err-reason">{lane.evt}</span>
                      <span className="cell-meta">{lane.repo}</span>
                      <span className="ec-group__count">{rows.length}</span>
                    </header>
                    {rows.map((row) => (
                      <CodeEntry key={rowId(row)} row={row} showLane={false} />
                    ))}
                  </section>
                );
              })
            : sorted.map((row) => <CodeEntry key={rowId(row)} row={row} showLane />)}
        </div>
      )}

    </div>
  );
}

/**
 * One entry: identity on the left, the published copy on the right IN FULL — the
 * whole reason this stopped being a table. The left column is fixed-width so the
 * codes stay aligned down one edge and remain scannable, which is the only thing
 * the table was actually good at.
 *
 * The row carries what a CUSTOMER-facing reader needs and nothing else: the code,
 * how loud it is, the reason, and the copy. The help-centre link and
 * its editor live on the code's own page — an article is per CODE, not per reason,
 * so repeating it on every row restated the same fact up to four times and put a
 * button on all 26 rows to edit one shared value. The "Codes with no article" stat
 * carries the gap for the list; the page carries the fix.
 */
function CodeEntry({ row, showLane }: { row: Row; showLane: boolean }) {
  const { lane, entry } = row;
  return (
    <div className="ec-entry">
      <div className="ec-entry__id">
        <Link className="ec-code ec-code--link" to={`/error-codes/${encodeURIComponent(entry.code)}`}>
          {entry.code}
        </Link>
        <div className="ec-entry__meta">
          <span className={`ec-lvl ec-lvl--${entry.level}`}>{entry.level}</span>
          {/* Which of the two groups this code belongs to. An internal code is
              operator-only: it never reaches a customer, so seeing it here is the
              only place it appears. Fail-closed — isCustomerFacing treats a
              missing audience as internal. */}
          <span
            className={`badge badge--${isCustomerFacing(entry) ? 'customer' : 'internal'}`}
            title={
              isCustomerFacing(entry)
                ? 'Shown to customers in-product and by support'
                : 'Staff only — a customer hitting this sees the generic service-issue message'
            }
          >
            {isCustomerFacing(entry) ? 'customer' : 'internal'}
          </span>
          {/* Only when the list is flat. Under a lane heading it would repeat the
              heading on all 13 of its rows. */}
          {showLane && <span className="badge">{lane.key}</span>}
        </div>
        <div className="err-reason">{entry.reason}</div>
      </div>

      <div className="ec-entry__copy">
        <p>{entry.customerMessage}</p>
        {!isCustomerFacing(entry) && (
          // The row above is the OPERATOR's sentence. What the customer actually
          // sees is the shared generic one — showing both stops someone quoting
          // an internal explanation into a support reply.
          <p className="ec-entry__generic">
            Customer sees: “{GENERIC_CUSTOMER_MESSAGE}”
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Every lane that emits, whether or not it has a dictionary. Behind the view toggle
 * because it answers a different question from the dictionary — "which surfaces
 * emit at all, and which still owe us codes" — and stacking it under the entries on
 * first paint was half of what made this page unreadable. This one IS tabular: five
 * short values per row, no prose.
 */
function LaneRegistry({ withCodes }: { withCodes: Set<string> }) {
  const sort = useUrlSort('evt', 'asc', 'l');
  const rows = compareBy(
    LANE_REGISTRY,
    (l) =>
      ({
        evt: l.evt,
        repo: l.repo,
        lane: l.lane,
        transport: l.transport,
        codes: withCodes.has(l.evt) ? 0 : 1,
      })[sort.key],
    sort.dir,
  );

  return (
    <>
      <table className="data-list--primary">
        <thead>
          <tr>
            <SortHeader label="evt" sortKey="evt" sort={sort} />
            <SortHeader label="Repo" sortKey="repo" sort={sort} />
            <SortHeader label="Lane" sortKey="lane" sort={sort} />
            <SortHeader label="Transport" sortKey="transport" sort={sort} />
            <SortHeader label="Codes" sortKey="codes" sort={sort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.evt} title={l.note ?? ''}>
              <td>
                <span className="err-reason">{l.evt}</span>
              </td>
              <td className="cell-meta">{l.repo}</td>
              <td className="cell-meta">{l.lane}</td>
              <td className="cell-meta">{l.transport === 'faro' ? '| logfmt' : '| regexp'}</td>
              <td>
                {withCodes.has(l.evt) ? (
                  <span className="mtx-yes">yes</span>
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="docs-note">
        {LANE_REGISTRY.filter((l) => l.evt.endsWith('_fail')).length} lanes are{' '}
        <code>*_fail</code>, {LANE_REGISTRY.filter((l) => l.evt.endsWith('_error')).length} are{' '}
        <code>*_error</code>. A rule that assumes <code>_fail</code> silently matches
        nothing against the Faro lanes.
      </p>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}
